import personalFinance from "assets/svg/personalFinance.jpg";
import autoLeasing from "assets/svg/autoLeasing.jpg";
import NoApplicationsFoundImg from "assets/svg/NoApplicationsFound.png";
import NoLetterFoundImg from "assets/svg/NoLetterFound.png";

// Per-service artwork — one image per catalog id, used for both the card
// thumbnail (Home carousel + View All list) and the detail page's left banner.
import FinanceRestructureImg from "assets/svg/FinanceRestructure.png";
import TopupImg from "assets/svg/Topup.png";
import FinanceTransferImg from "assets/svg/FinanceTransfer.png";
import EarlySettlementImg from "assets/svg/EarlySettlement.png";
import PartialSettlementImg from "assets/svg/PartialSettlement.png";
import FinanceCancellationImg from "assets/svg/FinanceCancellation.png";
import BuyOutImg from "assets/svg/BuyOut.png";
import ClearanceLetterImg from "assets/svg/ClearanceLetter.png";
import DebitProofLetterImg from "assets/svg/DebitProofLetter.png";
import ThreeMonthAuthorizationImg from "assets/svg/ThreeMonthAuthorization.png";
import OwnershipTransferImg from "assets/svg/OwnershipTransfer.png";
import ThreeMonthInsuranceImg from "assets/svg/ThreeMonthInsurance.png";
import BorrowSpareKeyImg from "assets/svg/BorrowSpareKey.png";
import AddActualDriverImg from "assets/svg/AddActualDriver.png";

// Region icons for the Vehicle Services that are sold per territory
// (3-Month Authorization / 3-Month Insurance).
import BahrainAuthorIcon from "assets/icons/BahrainAuthor.svg";
import GCCAuthorIcon from "assets/icons/GCCAuthor.svg";
import JordanAuthorIcon from "assets/icons/JordanAuthor.svg";

// Two service buckets, surfaced as the two tabs on the "View All" page.
export const OTHER_SERVICES_CATEGORY = {
  OTHER: "other",
  VEHICLE: "vehicle",
};

// Vocabulary for the application-selection → form → submit experience
// (FinanceServiceFlow.jsx). Centralized here so a service's catalog entry and
// the flow engine's per-type config (which fields to show, which mutation to
// call) both reference the same constant instead of duplicated string literals.
export const FLOW_TYPE = {
  RESTRUCTURE: "restructure",
  TOPUP: "topup",
  EARLY_SETTLEMENT: "earlySettlement",
  BUYOUT: "buyout",
  CANCELLATION: "cancellation",
  PARTIAL_SETTLEMENT: "partialSettlement",
  CLEARANCE_LETTER: "clearanceLetter",
  ADHOC_CHARGE: "adhocCharge",
};

// Partial settlement shape: repaymentType picks the Temenos route server-side,
// recalculationMethod picks whether the saving shortens the term or the instalment.
// Values must match the backend's strict LOVs (partialSettlementApp validation).
export const PARTIAL_REPAYMENT_TYPES = [
  { value: "Principal Decrease", labelKey: "OTHER_SERVICES.REPAYMENT_PRINCIPAL_DECREASE" },
  { value: "Compression Repayment", labelKey: "OTHER_SERVICES.REPAYMENT_COMPRESSION" },
];

export const PARTIAL_RECALCULATION_METHODS = [
  { value: "TERM", labelKey: "OTHER_SERVICES.RECALC_TERM" },
  { value: "INSTALLMENT", labelKey: "OTHER_SERVICES.RECALC_INSTALLMENT" },
];

// Region choices for the per-territory Vehicle Services. `chargeType` is the
// exact Temenos LOV value the backend allow-lists (ADHOC_CHARGE_TYPES in
// v2/apis/validations/app.validation.js) — an unlisted value is rejected with a
// 400, so these strings must match it character for character.
//
// ⚠️ 3-Month Authorization has no `.BAHRAIN` variant in that allowlist (only
// the bare value, `.GCC` and `.JORDEN`), while Insurance Extension does. The
// design shows Bahrain/GCC/Jordan for BOTH, so Bahrain is mapped to the bare
// `3MAUTHORIZATION` here — confirm with Temenos and change this one line if a
// dedicated Bahrain code exists.
const AUTHORIZATION_REGIONS = [
  { id: "bahrain", labelKey: "OTHER_SERVICES.REGION_BAHRAIN", icon: BahrainAuthorIcon, chargeType: "3MAUTHORIZATION" },
  { id: "gcc", labelKey: "OTHER_SERVICES.REGION_GCC", icon: GCCAuthorIcon, chargeType: "3MAUTHORIZATION.GCC" },
  { id: "jordan", labelKey: "OTHER_SERVICES.REGION_JORDAN", icon: JordanAuthorIcon, chargeType: "3MAUTHORIZATION.JORDEN" },
];

const INSURANCE_REGIONS = [
  { id: "bahrain", labelKey: "OTHER_SERVICES.REGION_BAHRAIN", icon: BahrainAuthorIcon, chargeType: "INSURANCEEXTENSION.BAHRAIN" },
  { id: "gcc", labelKey: "OTHER_SERVICES.REGION_GCC", icon: GCCAuthorIcon, chargeType: "INSURANCEEXTENSION.GCC" },
  { id: "jordan", labelKey: "OTHER_SERVICES.REGION_JORDAN", icon: JordanAuthorIcon, chargeType: "INSURANCEEXTENSION.JORDEN" },
];

// Shared Temenos vocabulary for every ad-hoc charge request — only chargeType
// differs per service/region.
const ADHOC_DETAILS = { requestType: "ADHOC.CHARGES", sourceOfIncome: "SALARIED" };

// Category-level fallback — only used if a catalog item is ever added without
// its own dedicated image in SERVICE_IMAGES below.
const DEFAULT_IMAGE = {
  [OTHER_SERVICES_CATEGORY.OTHER]: personalFinance,
  [OTHER_SERVICES_CATEGORY.VEHICLE]: autoLeasing,
};

// Single source of truth for each service's artwork — keyed by catalog id so
// both the card image and the detail-page banner stay in sync automatically.
const SERVICE_IMAGES = {
  "loan-restructure": FinanceRestructureImg,
  topup: TopupImg,
  "loan-transfer": FinanceTransferImg,
  "early-settlement": EarlySettlementImg,
  "partial-settlement": PartialSettlementImg,
  "loan-cancelation": FinanceCancellationImg,
  "buy-out": BuyOutImg,
  "clearance-letter": ClearanceLetterImg,
  "debit-proof-letter": DebitProofLetterImg,
  "three-month-authorization": ThreeMonthAuthorizationImg,
  "ownership-transfer": OwnershipTransferImg,
  "three-month-insurance": ThreeMonthInsuranceImg,
  "borrow-spare-key": BorrowSpareKeyImg,
  "add-actual-driver": AddActualDriverImg,
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared placeholder CONTENT for the product detail pages.
// ⚠️ TEMPORARY — stands in until a real per-service content API exists (same
// approach as the dynamic vendors: nameEn/nameAr-style bilingual fields inline).
// The description + bullets are generic placeholders; replace with real copy /
// API content later. Each *_En/_Ar pair is picked by language via pickLang().
// ─────────────────────────────────────────────────────────────────────────────
const SUBTITLE = {
  en: "Compatible With Islamic Law And With A Competitive Profit Margin",
  ar: "متوافق مع أحكام الشريعة الإسلامية وبهامش ربح تنافسي",
};

const DESCRIPTION = {
  en: "A fully digital, Sharia-compliant service. Complete your request in a few simple steps and track its status anytime. Designed to make managing your finance faster and easier.",
  ar: "خدمة رقمية بالكامل ومتوافقة مع أحكام الشريعة الإسلامية. أكمل طلبك في خطوات بسيطة وتابع حالته في أي وقت. صُممت لتجعل إدارة تمويلك أسرع وأسهل.",
};

const BULLETS = [
  { en: "Includes employees and retired individual.", ar: "يشمل الموظفين والمتقاعدين." },
  { en: "Age of applicant must be between 21 and 60", ar: "يجب أن يكون عمر المتقدم بين 21 و 60 عامًا" },
  { en: "Minimum salary of 3500 riyals.", ar: "الحد الأدنى للراتب 3500 ريال." },
  {
    en: "The minimum financing amount is not less than 10,000 riyals, and the maximum financing amount is 250,000 riyals.",
    ar: "الحد الأدنى لمبلغ التمويل لا يقل عن 10,000 ريال، والحد الأقصى لمبلغ التمويل 250,000 ريال.",
  },
];

// Right-side "not available" empty-state variants.
const EMPTY_STATE = {
  APPLICATION: {
    titleEn: "No Application Found",
    titleAr: "لا يوجد طلب",
    subEn:
      "No applications are available at the moment. Start a new application to see it listed here.",
    subAr: "لا توجد طلبات متاحة في الوقت الحالي. ابدأ طلبًا جديدًا لعرضه هنا.",
    emptyStateImage: NoApplicationsFoundImg
  },
  CLEARANCE_LETTER: {
    titleEn: "No Clearance Letter Available",
    titleAr: "لا يوجد خطاب إخلاء طرف",
    subEn:
      "There is currently no clearance letter available right now. Once a clearance letter is issued, it will appear here for download and review.",
    subAr:
      "لا يوجد حاليًا خطاب إخلاء طرف متاح. بمجرد إصدار خطاب إخلاء الطرف، سيظهر هنا للتنزيل والمراجعة.",
    emptyStateImage: NoLetterFoundImg
  },
  DEBIT_PROOF_LETTER: {
    titleEn: "No Debt Proof Letter Available",
    titleAr: "لا يوجد خطاب إثبات المديونية",
    subEn:
      "There is currently no Debt Proof Letter. Once a debt proof letter is issued, it will appear here for download and review.",
    subAr:
      "لا يوجد حاليًا خطاب إثبات المديونية. بمجرد إصداره، سيظهر هنا للتنزيل والمراجعة.",
    emptyStateImage: NoLetterFoundImg
  },
};

// ⚠️ TEMPORARY hardcoded catalog — stands in for a future GET /services API.
// `comingSoon` drives the card click: true → Coming Soon modal, false → detail
// page (routes.OTHER_SERVICES + "/:id"). Shape mirrors the vendor API so the
// switch to live data is a drop-in.
const RAW_ITEMS = [
  // ── Other Services (Restructure/Topup enabled; the rest stay Coming Soon
  // until enabled manually later) ─────────────────────────────────────────
  { id: "loan-restructure", nameEn: "Finance Restructure", nameAr: "إعادة هيكلة التمويل", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "topup", nameEn: "Topup", nameAr: "زيادة التمويل", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "early-settlement", nameEn: "Early Settlement", nameAr: "السداد المبكر", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "partial-settlement", nameEn: "Partial Settlement", nameAr: "السداد الجزئي", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "loan-cancelation", nameEn: "Finance Cancelation", nameAr: "إلغاء التمويل", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "buy-out", nameEn: "Buy-Out", nameAr: "شراء المديونية", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "clearance-letter", nameEn: "Clearance Letter", nameAr: "خطاب إخلاء طرف", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: false },
  { id: "debit-proof-letter", nameEn: "Debt Proof Letter", nameAr: "خطاب إثبات المديونية", category: OTHER_SERVICES_CATEGORY.OTHER, comingSoon: true },
  // ── Vehicle Services ────────────────────────────────────────────
  // The four ad-hoc charge services are live (they all submit to
  // /v2/app/adhoc-app with a different chargeType). Finance Transfer and
  // Ownership Transfer stay Coming Soon — neither has a backend endpoint.
  { id: "loan-transfer", nameEn: "Finance Transfer", nameAr: "تحويل التمويل", category: OTHER_SERVICES_CATEGORY.VEHICLE, comingSoon: true },
  { id: "three-month-authorization", nameEn: "3-Month Authorization", nameAr: "تفويض لمدة 3 أشهر", category: OTHER_SERVICES_CATEGORY.VEHICLE, comingSoon: false },
  { id: "ownership-transfer", nameEn: "Ownership Transfer", nameAr: "نقل الملكية", category: OTHER_SERVICES_CATEGORY.VEHICLE, comingSoon: true },
  { id: "three-month-insurance", nameEn: "3-Month Insurance", nameAr: "تأمين لمدة 3 أشهر", category: OTHER_SERVICES_CATEGORY.VEHICLE, comingSoon: false },
  { id: "borrow-spare-key", nameEn: "Borrow Spare Key", nameAr: "استعارة المفتاح الاحتياطي", category: OTHER_SERVICES_CATEGORY.VEHICLE, comingSoon: false },
  { id: "add-actual-driver", nameEn: "Add Actual Driver", nameAr: "إضافة السائق الفعلي", category: OTHER_SERVICES_CATEGORY.VEHICLE, comingSoon: false },
];

// Resolve each item's card image (own image → category default → null).
export const OTHER_SERVICES_ITEMS = RAW_ITEMS.map((item) => ({
  ...item,
  image: SERVICE_IMAGES[item.id] || DEFAULT_IMAGE[item.category] || null,
}));

// ─────────────────────────────────────────────────────────────────────────────
// Detail-page CONTENT keyed by service id (the "get by id" payload; hardcoded
// until an API exists). Only services with comingSoon:false need an entry here.
// `heading` overrides the banner title for the section heading below the banner
// (defaults to the catalog name when omitted). bannerImage comes straight from
// SERVICE_IMAGES; emptyStateImage is the shared "no results" illustration.
// ─────────────────────────────────────────────────────────────────────────────
const baseDetail = (id, emptyState, extra = {}) => ({
  subtitle: SUBTITLE,
  description: DESCRIPTION,
  bullets: BULLETS,
  emptyState,
  bannerImage: SERVICE_IMAGES[id] || null,
  emptyStateImage: emptyState.emptyStateImage || NoApplicationsFoundImg,
  ...extra,
});

const SERVICE_DETAILS = {
  // `flow` enables the application-selection → form → submit experience on the
  // right side (empty state shows only when the user has no SIGNED contracts).
  // Which fields/uploads a flow.type needs is decided in one place —
  // FLOW_CONFIG in FinanceServiceFlow.jsx — so entries here only carry the
  // type + whatever fixed payload values that type requires.
  "loan-restructure": baseDetail("loan-restructure", EMPTY_STATE.APPLICATION, {
    flow: { type: FLOW_TYPE.RESTRUCTURE, restructureType: "Restruecture" },
  }),
  topup: baseDetail("topup", EMPTY_STATE.APPLICATION, {
    flow: { type: FLOW_TYPE.TOPUP, restructureType: "Topup" },
  }),
  "early-settlement": baseDetail("early-settlement", EMPTY_STATE.APPLICATION, {
    flow: { type: FLOW_TYPE.EARLY_SETTLEMENT },
  }),
  // sourceOfIncome is derived from the chosen repaymentType at submit time
  // ('PARTIAL PD API' / 'PARTIAL CR API'), so it isn't fixed here.
  "partial-settlement": baseDetail("partial-settlement", EMPTY_STATE.APPLICATION, {
    flow: {
      type: FLOW_TYPE.PARTIAL_SETTLEMENT,
      details: {
        requestType: "PARTIAL",
        settlementType: "PARTIAL SETTLEMENT",
        aaClosureReason: "",
        prPayoffStatus: "",
      },
    },
  }),
  "loan-cancelation": baseDetail("loan-cancelation", EMPTY_STATE.APPLICATION, {
    flow: { type: FLOW_TYPE.CANCELLATION },
  }),
  // Buy-Out has no application picker: the debt sits at another bank, so the
  // customer enters the other-bank details, then their own amount/IBAN/tenure.
  "buy-out": baseDetail("buy-out", EMPTY_STATE.APPLICATION, {
    flow: { type: FLOW_TYPE.BUYOUT },
  }),
  // Document service: no form, no submit — selecting an application requests
  // the letter and renders the returned PDF. `documentBaseName` names the
  // downloaded file (the API returns content only, no fileName).
  "clearance-letter": baseDetail("clearance-letter", EMPTY_STATE.CLEARANCE_LETTER, {
    heading: { en: "Finance Clearance Letter", ar: "خطاب إخلاء طرف التمويل" },
    flow: { type: FLOW_TYPE.CLEARANCE_LETTER, documentBaseName: "ClearanceLetter" },
  }),
  "debit-proof-letter": baseDetail("debit-proof-letter", EMPTY_STATE.DEBIT_PROOF_LETTER),

  // ── Vehicle Services ───────────────────────────────────────────────────────
  // All four are ad-hoc charges against an existing application: pick a region
  // (where the service is sold per territory), pick an application, submit.
  // `regions` is what adds the region step — omit it and the flow starts at the
  // application picker.
  "three-month-authorization": baseDetail("three-month-authorization", EMPTY_STATE.APPLICATION, {
    flow: {
      type: FLOW_TYPE.ADHOC_CHARGE,
      regionTitleKey: "OTHER_SERVICES.SELECT_REGION",
      regionLabelKey: "OTHER_SERVICES.THREE_MONTH_AUTHORIZATION",
      regions: AUTHORIZATION_REGIONS,
      details: ADHOC_DETAILS,
    },
  }),
  "three-month-insurance": baseDetail("three-month-insurance", EMPTY_STATE.APPLICATION, {
    flow: {
      type: FLOW_TYPE.ADHOC_CHARGE,
      regionTitleKey: "OTHER_SERVICES.SELECT_REGION",
      regionLabelKey: "OTHER_SERVICES.THREE_MONTH_INSURANCE",
      regions: INSURANCE_REGIONS,
      details: ADHOC_DETAILS,
    },
  }),
  "borrow-spare-key": baseDetail("borrow-spare-key", EMPTY_STATE.APPLICATION, {
    flow: {
      type: FLOW_TYPE.ADHOC_CHARGE,
      chargeType: "BORROWOFSPEARKEY",
      details: ADHOC_DETAILS,
    },
  }),
  "add-actual-driver": baseDetail("add-actual-driver", EMPTY_STATE.APPLICATION, {
    flow: {
      type: FLOW_TYPE.ADHOC_CHARGE,
      chargeType: "ADDACTUALDRIVER",
      details: ADHOC_DETAILS,
    },
  }),
};

export const getServiceById = (id) =>
  OTHER_SERVICES_ITEMS.find((item) => item.id === id) || null;

export const getServiceDetail = (id) => SERVICE_DETAILS[id] || null;

export const getServicesByCategory = (category) =>
  OTHER_SERVICES_ITEMS.filter((item) => item.category === category);

// Localized name accessor — mirrors vendorName() in the V2 vendor components.
export const serviceName = (item, isArabic) =>
  (isArabic ? item?.nameAr : item?.nameEn) || item?.nameEn || item?.nameAr || "-";

// Pick the active-language string from a { en, ar } content object.
export const pickLang = (obj, isArabic) => {
  if (!obj) return "";
  return (isArabic ? obj.ar : obj.en) || obj.en || obj.ar || "";
};
